import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { analyticsApi } from '../services/api';
import { Loader } from 'lucide-react';

export const AnalyticsDashboardPage: React.FC = () => {
  const [rankings, setRankings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      setIsLoading(true);
      const response = await analyticsApi.getTeamRankings(10);
      setRankings(response.data.data || []);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="glass p-6">
          <h1 className="section-title">Analytics Dashboard</h1>
          <p className="text-gray-400">Team rankings, statistics, and performance analytics</p>
        </div>
      </motion.div>

      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass p-4 mb-4 bg-red-500/10 border border-red-500/30 text-red-300">
          {error}
        </motion.div>
      )}

      {isLoading ? (
        <div className="glass p-12 flex justify-center">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 20 }} className="glass p-6">
            <h2 className="subsection-title">Top Teams by Contribution</h2>
            <div className="space-y-2">
              {rankings.length > 0 ? (
                rankings.map((team, idx) => (
                  <div key={team.team_number} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-blue-400 w-8">#{idx + 1}</span>
                      <span className="font-semibold">Team {team.team_number}</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-gray-400">{team.matches_scored} matches</span>
                      <span className="font-bold text-blue-300">{team.total_contribution.toFixed(1)} pts</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400">No data available. Start scouting teams to see rankings!</p>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 20 }} transition={{ delay: 0.1 }} className="glass p-6 mt-4">
            <h2 className="subsection-title">Features</h2>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li>✓ Real-time team rankings by contribution</li>
              <li>✓ Individual team statistics and performance trends</li>
              <li>✓ Match prediction engine</li>
              <li>✓ Team comparison tools</li>
              <li>✓ Radar charts for team capabilities</li>
              <li>✓ Historical performance trends</li>
            </ul>
          </motion.div>
        </>
      )}
    </div>
  );
};
